// Billing math — shared between job completion (auto-compute) and the
// Review modal (display + recompute when the owner edits hours/miles/fee).
//
// Formula (per the Malca / Orion rate cards):
//   total = (flatFee × numGuards) + expenses     IF flatFee > 0
//         ELSE
//           hourlyRate × max(hours, minHours) × numGuards
//         + mileageRate × miles × mileageSets
//         + expenses
//
// Multi-guard logic:
//   - Each assigned guard files their own activity log → bills hours separately.
//     Total hours $ = hourly × hoursCharged × numGuards.
//   - Drivers are guards #1 and #3. Only drivers submit mileage.
//     mileageSets = 1 for 1–2 guards, 2 for 3–4 guards.
//     Total mileage $ = mileage × miles × mileageSets.
//   - Expenses are billed once across the job (not per guard).
//
// Hours are computed from (completedAt − startedAt) in hours, rounded to 2 decimals.
// Miles come from the owner's review-modal edits or auto-Maps mileage.

function tsToMs(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (typeof ts === "string") {
    const n = Date.parse(ts);
    return Number.isNaN(n) ? null : n;
  }
  if (ts.toMillis) return ts.toMillis();
  if (ts.seconds != null) return ts.seconds * 1000;
  return null;
}

export function hoursBetween(startedAt, completedAt) {
  const a = tsToMs(startedAt);
  const b = tsToMs(completedAt);
  if (a == null || b == null || b <= a) return 0;
  return Math.round(((b - a) / 3600000) * 100) / 100;
}

/**
 * Nights a piece sat in the vault — calendar days from the day it was
 * vaulted to the day it's delivered, minimum 1. Sat → Mon is 2 nights.
 *
 * `starts` is one value or a list of ISO timestamps / "YYYY-MM-DD"
 * strings; the EARLIEST wins. Always pass BOTH the piece's `heldAt` and
 * the vaulting job's `scheduledDate`: holding is normally flagged during
 * billing, days after the run, so `heldAt` alone undercounts a back-dated
 * job (a Saturday job flagged on Thursday reads as 1 night, not 2). A
 * piece carried over from an older job has the earlier `heldAt`, and that
 * then wins instead.
 */
export function vaultNightsBetween(starts, endYmd) {
  // Collapse to local midnight so partial days never shift the count.
  const midnight = (v) => {
    if (!v) return null;
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v));
    if (ymd) return new Date(+ymd[1], +ymd[2] - 1, +ymd[3]).getTime();
    const d = new Date(v);
    return Number.isNaN(d.getTime())
      ? null
      : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };
  const end = midnight(endYmd);
  const from = (Array.isArray(starts) ? starts : [starts]).map(midnight).filter((t) => t != null);
  if (end == null || !from.length) return 1;
  return Math.max(1, Math.round((end - Math.min(...from)) / 86400000));
}

// Drivers are guards #1 and #3. Pairs of guards share a vehicle:
//   1 guard  → 1 driver (#1)  → 1 mileage set
//   2 guards → 1 driver (#1)  → 1 mileage set
//   3 guards → 2 drivers (#1, #3) → 2 mileage sets
//   4 guards → 2 drivers (#1, #3) → 2 mileage sets
export function mileageSetsFor(numGuards) {
  const n = Math.max(1, Number(numGuards) || 1);
  return n >= 3 ? 2 : 1;
}

export function isDriverGuard(guardNumber) {
  const n = Number(guardNumber);
  return n === 1 || n === 3;
}

// Whether a guard drives their own car (and therefore bills mileage). Honors
// the explicit per-guard `ownCar` toggle; falls back to the legacy lead/chase
// rule (G1/G3 drive, G2/G4 ride along) when it hasn't been set.
export function guardOwnsCar(assignment, guardNumber) {
  if (assignment && assignment.ownCar != null) return !!assignment.ownCar;
  return isDriverGuard(guardNumber);
}

// How many cars bill mileage on this job = the number of own-car guards.
// With default toggles this equals mileageSetsFor() (G1/G3 drive), so it's
// backward compatible; once the owner flips a passenger to own-car, that car's
// mileage gets billed too.
export function ownCarCount(job) {
  const numGuards = Math.max(1, Number(job?.numGuards) || 1);
  const assignments = Array.isArray(job?.guardAssignments) ? job.guardAssignments : [];
  let count = 0;
  for (let n = 1; n <= numGuards; n++) {
    const a = assignments.find((g) => g.guardNumber === n);
    if (guardOwnsCar(a, n)) count++;
  }
  return Math.max(1, count);
}

// Per-stop guard coverage. A stop with no explicit `guardNumbers` is covered
// by every guard on the job (the default, and how all legacy jobs behave).
// Once the owner assigns specific guards to a stop, only those guards cover
// it — they're the only ones who see it on the phone and bill for it.
export function stopCoveredByGuard(stop, guardNumber) {
  const list = stop && stop.guardNumbers;
  if (!Array.isArray(list) || list.length === 0) return true;
  return list.includes(Number(guardNumber));
}

export function computeJobBilling(job, broker, overrides = {}) {
  const hourlyRate = Number(broker?.hourlyRate) || 0;
  const mileageRate = Number(broker?.mileageRate) || 0;
  const minHours = Number(broker?.minHours) || 0;
  // Flat fee can be overridden per-job (e.g. a DAI run that's
  // unusually long and gets a higher flat rate that week). Lookup
  // order: per-job override > broker default. Owner sets the override
  // in the Review billing modal; falls back to the broker rate card
  // value when the field is empty.
  const flatFee = overrides.flatFeeOverride != null && overrides.flatFeeOverride !== ""
    ? Number(overrides.flatFeeOverride)
    : Number(job?.flatFeeOverride) || Number(broker?.flatFee) || 0;

  // Allow owner to override hours/miles/expenses in the Review modal
  const rawHours = overrides.hours != null ? Number(overrides.hours) : hoursBetween(job?.startedAt, job?.completedAt || overrides.completedAt);
  const miles = overrides.miles != null ? Number(overrides.miles) : (Number(job?.miles) || 0);
  // `!= null` like hours/miles above, NOT `||`: zero is a real override. The
  // review panel sends the live expense total, so deleting the last expense
  // line sends 0 — with `||` that read as "no override given" and fell back
  // to the stored job.expenses, so a removed expense kept billing forever
  // with nothing on screen to explain it.
  const expenses = overrides.expenses != null
    ? (Number(overrides.expenses) || 0)
    : (Number(job?.expenses) || 0);

  const numGuards = Math.max(1, Number(overrides.numGuards ?? job?.numGuards) || 1);
  // Mileage sets = number of cars billing mileage. Derived from the own-car
  // toggles (default G1/G3 drive → same as the old pair rule). An explicit
  // override wins so the review can force a value.
  const mileageSets = overrides.mileageSets != null
    ? Math.max(1, Number(overrides.mileageSets))
    : ownCarCount({ numGuards, guardAssignments: job?.guardAssignments });

  const hoursCharged = Math.max(rawHours, minHours);
  const minHoursApplied = hoursCharged > rawHours;

  let total;
  const lineItems = [];

  if (flatFee > 0 && overrides.useFlatFee !== false) {
    const flatTotal = Math.round(flatFee * numGuards * 100) / 100;
    total = Math.round((flatTotal + expenses) * 100) / 100;
    // Only include `detail` when there's something useful to say. Firestore
    // rejects undefined values when this lineItem is later persisted as part
    // of billingBreakdown, so never emit `detail: undefined`.
    const flatLine = {
      label: numGuards > 1 ? `Flat fee × ${numGuards} guards` : "Flat fee",
      amount: flatTotal,
    };
    if (numGuards > 1) flatLine.detail = `$${flatFee.toFixed(2)} × ${numGuards}`;
    lineItems.push(flatLine);
    if (expenses) lineItems.push({ label: "Expenses", amount: expenses });
  } else {
    // Per-guard hours: when the caller passes a `guardHours` array, render
    // one Hourly line per guard so the preview matches what
    // createInvoicesForJob will actually bill (split-times scenario where
    // one guard goes back to home earlier than the rest). Falls back to a
    // single combined line when every guard ran the full route.
    const guardHoursArr = Array.isArray(overrides.guardHours)
      && overrides.guardHours.length === numGuards
      ? overrides.guardHours.map((h) => {
          const v = h == null ? hoursCharged : Math.max(Number(h), minHours);
          return Number.isFinite(v) ? v : hoursCharged;
        })
      : null;
    const splitInUse = guardHoursArr
      && guardHoursArr.some((h) => Math.abs(h - hoursCharged) > 0.001);
    const hourlyAmount = guardHoursArr
      ? Math.round(guardHoursArr.reduce((s, h) => s + hourlyRate * h, 0) * 100) / 100
      : Math.round(hourlyRate * hoursCharged * numGuards * 100) / 100;
    // Per-guard miles: when the caller passes a `guardMiles` array (one entry
    // per guard, 0 for passengers), bill each car its own distance — so a
    // guard who covered fewer stops bills less. Falls back to the shared
    // miles × mileageSets model when not provided.
    const guardMilesArr = Array.isArray(overrides.guardMiles) && overrides.guardMiles.length === numGuards
      ? overrides.guardMiles.map((m) => Number(m) || 0)
      : null;
    const billedMiles = guardMilesArr
      ? guardMilesArr.reduce((s, m) => s + m, 0)
      : miles * mileageSets;
    const mileageAmount = Math.round(mileageRate * billedMiles * 100) / 100;
    total = Math.round((hourlyAmount + mileageAmount + expenses) * 100) / 100;
    if (splitInUse) {
      guardHoursArr.forEach((h, i) => {
        const amt = Math.round(hourlyRate * h * 100) / 100;
        lineItems.push({
          label: `Hourly · G${i + 1} (${h.toFixed(2)}h)`,
          detail: `${h.toFixed(2)}h × $${hourlyRate.toFixed(2)}/hr`,
          amount: amt,
        });
      });
    } else {
      lineItems.push({
        label: `Hourly${numGuards > 1 ? ` × ${numGuards} guards` : ""} (${hoursCharged.toFixed(2)}h${minHoursApplied ? ` — ${minHours}h min applied` : ""})`,
        detail: `${hoursCharged.toFixed(2)}h × $${hourlyRate.toFixed(2)}/hr${numGuards > 1 ? ` × ${numGuards}` : ""}`,
        amount: hourlyAmount,
      });
    }
    if (billedMiles > 0 && mileageRate > 0) {
      const positive = guardMilesArr ? guardMilesArr.filter((m) => m > 0) : [];
      const milesDiffer = positive.length > 1 && new Set(positive).size > 1;
      if (guardMilesArr && milesDiffer) {
        // Cars drove different distances (partial overlap) — one line each.
        guardMilesArr.forEach((m, i) => {
          if (m > 0) lineItems.push({
            label: `Mileage · G${i + 1} (${Math.round(m)} mi)`,
            detail: `${Math.round(m)} mi × $${mileageRate.toFixed(3)}/mi`,
            amount: Math.round(mileageRate * m * 100) / 100,
          });
        });
      } else {
        lineItems.push({
          label: `Mileage${mileageSets > 1 ? ` × ${mileageSets} sets` : ""}`,
          detail: guardMilesArr
            ? `${Math.round(billedMiles)} mi total × $${mileageRate.toFixed(3)}/mi`
            : `${miles} mi × $${mileageRate.toFixed(3)}/mi${mileageSets > 1 ? ` × ${mileageSets}` : ""}`,
          amount: mileageAmount,
        });
      }
    }
    if (expenses) lineItems.push({ label: "Expenses", amount: expenses });
  }

  return {
    total,
    rawHours,
    hoursCharged,
    minHoursApplied,
    miles,
    expenses,
    hourlyRate,
    mileageRate,
    minHours,
    flatFee,
    numGuards,
    mileageSets,
    lineItems,
  };
}
